import { Resources } from 'constants/resources';

import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { OrderProviderExternalErrorsWidgetsNames } from 'pages/Monitoring/OrderProviderExternalErrors/constants/widgetsNames';
import { useGetOrderProviderExternalErrorsChartData } from 'pages/Monitoring/OrderProviderExternalErrors/hooks/useGetOrderProviderExternalErrorsChartData';
import { OrderProviderExternalErrorsFilterValues } from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsFilter/OrderProviderExternalErrorsFilter';
import {
  StyledBox,
  StyledDatagrid,
  StyledDialog,
} from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsSummary/OrderProviderExternalErrorsSummary.styled';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Link as RouterLink } from 'react-router-dom';
import { Alert } from 'shared/mui/Alert/Alert';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Link } from 'shared/mui/Link/Link';
import { Typography } from 'shared/mui/Typography';
import { FunctionField } from 'shared/react-admin/Fields';
import { Gateway } from 'types/embedded/gateway';
import { convertReactAdminIdToHydraId } from 'utils';
import { getReportPeriod } from 'utils/getReportPeriod';

import { OrderProviderExternalErrorsSummaryDialogSkeleton } from './OrderProviderExternalErrorsSummaryDialogSkeleton';

type Props = {
  filterValues: OrderProviderExternalErrorsFilterValues;
  handleOpenGatewaySummaryDialog: (record: Gateway) => void;
} & DialogProps;

export const OrderProviderExternalErrorsSummaryDialog: React.FC<Props> = ({
  filterValues,
  open,
  onClose,
  handleOpenGatewaySummaryDialog,
}): JSX.Element => {
  const { createPathWithQuery } = useCreatePathWithQuery();
  const translate = useTypedTranslate();
  const { data, isLoading, error } = useGetOrderProviderExternalErrorsChartData(filterValues, [
    OrderProviderExternalErrorsWidgetsNames.RatioByGateways,
  ]);

  const reportPeriod = getReportPeriod({
    dateGte: filterValues?.createdAt?.gte,
    dateLte: filterValues?.createdAt?.lte,
    timezone: filterValues?.timezone,
  });

  const summaryTitle = translate(
    'app.orderProviderExternalErrors.summaryDeclineReasonsByGateways',
    { reportPeriod },
  );
  const summarySubtitle = translate(
    'app.orderProviderExternalErrors.summaryDeclineReasonsByGatewaysDescription',
    { reportPeriod },
  );

  return (
    <StyledDialog onClose={onClose} open={open}>
      <CardHeader
        appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
        sx={{ padding: '24px' }}
        title={summaryTitle}
        titleSize="smallBold"
      />
      <CardContent sx={{ padding: '0 24px !important' }}>
        <StyledBox>
          {!error && <Typography sx={{ marginBottom: '24px' }}>{summarySubtitle}</Typography>}
          {isLoading ? (
            <OrderProviderExternalErrorsSummaryDialogSkeleton />
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <StyledDatagrid
              bulkActionButtons={false}
              data={data?.ratio_by_gateways?.ratioByGateways}
              empty={<></>}
              isLoading={isLoading}
            >
              <FunctionField
                label={translate('app.table.provider')}
                render={(record) => record.provider.name}
              />
              <FunctionField
                label={translate('app.table.gateway')}
                render={(record) => (
                  <Link
                    component={RouterLink}
                    sx={{ color: 'primary.main' }}
                    to={createPathWithQuery({
                      resource: Resources.Gateways,
                      type: 'show',
                      id: convertReactAdminIdToHydraId(record.gateway.id, Resources.Gateways),
                    })}
                  >
                    {record.gateway.name}
                  </Link>
                )}
              />
              <FunctionField
                label={translate('app.orderProviderExternalErrors.datagrid.numberOfDeclines')}
                render={(record) =>
                  record.errors ? (
                    <>
                      {record.errors}{' '}
                      <Link
                        onClick={() => {
                          handleOpenGatewaySummaryDialog(record.gateway);
                          onClose();
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        {translate('app.orderProviderExternalErrors.details')}
                      </Link>
                    </>
                  ) : (
                    0
                  )
                }
              />
              <FunctionField
                label={translate('app.orderProviderExternalErrors.datagrid.percentOfDeclines')}
                render={(record) => record.percentageRatio}
              />
            </StyledDatagrid>
          )}
        </StyledBox>
      </CardContent>
      <CardActionsButtons
        rightActionsSlot={
          <Button label={translate('app.buttons.close')} onClick={onClose} variant="outlined" />
        }
      />
    </StyledDialog>
  );
};
